import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { validateField, formatApiError } from '@/lib/validators';
import { canSeeFinancials as userCanSeeFinancials } from '@/lib/constants';
import { formatIST } from '@/lib/utils';
import { isMac } from '@/lib/mnemonics/keys';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import BuyerPanel from './CreateOrder/BuyerPanel';
import ItemsGrid from './CreateOrder/ItemsGrid';
import OrderStrip from './CreateOrder/OrderStrip';
import { BLANK_ITEM, calcItemTotal } from './CreateOrder/itemPricing';

import { Screen } from '@/components/data';

/**
 * Correcting a booked order, on the same page that took it.
 *
 * Order entry happens with the customer on the phone, which is where the
 * mistakes are made — a quantity a digit short, a rate in the wrong row, an item
 * the customer adds on a second call. This is that page again, filled in, with
 * the parts the floor has already acted on held still: an item whose batch has
 * moved keeps its quantity, and only an admin drops a row.
 */

/** Has anything actually been made against this item? The backend decides the same way. */
function itemStarted(item) {
  if (item.status === 'completed' || item.delivery_status === 'delivered') return true;
  if (item.is_split) return true;
  const batches = item.batches || [];
  if (batches.length > 1) return true;
  return batches.some(b =>
    (b.current_stage_index || 0) > 0 || (b.stage_history || []).length > 0 || b.status === 'completed'
  );
}

/** A stored date, however it was written, as a date input takes it. */
function dateInputValue(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function itemToRow(item) {
  return {
    ...BLANK_ITEM,
    id: item.id,
    item_name: item.item_name || item.name || '',
    quantity: String(item.quantity ?? ''),
    price_type: item.price_type || 'total',
    item_value: item.item_value ?? '',
    price_per_piece: item.price_per_piece || '',
    price_per_sheet: item.price_per_sheet || '',
    pieces_per_sheet: item.pieces_per_sheet || '',
    expected_delivery_date: dateInputValue(item.expected_delivery_date),
    special_instructions: item.special_instructions || '',
    catalog_item_id: item.catalog_item_id || null,
    booked: true,
    started: itemStarted(item),
  };
}

export default function EditOrderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const canSeeFinancials = userCanSeeFinancials(user);
  const isAdmin = user?.role === 'admin';

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // a job split into production types is the floor's and its job card's; those
  // rows are left off the grid rather than shown as things the desk can reprice
  const [splitCount, setSplitCount] = useState(0);

  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerErrors, setNewCustomerErrors] = useState({});
  const [newCustomer, setNewCustomer] = useState({
    display_name: '', mobile_number: '', email: '',
    delivery_address: '', address: '',
  });
  const [deliveryAddress, setDeliveryAddress] = useState('');

  const [orderDetails, setOrderDetails] = useState({
    priority: 'medium', po_ref_number: '', po_date: '', order_source: '',
    discount_pct: '', gst_pct: '', remarks: '',
  });
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [lineItems, setLineItems] = useState([]);
  const [draftSeq, setDraftSeq] = useState(0);
  const draftId = `draft-${draftSeq}`;

  // remarks became a timeline of entries the moment somebody added one on the
  // order screen; only a plain narration is still editable here, or the edit
  // would flatten that history into a single string
  const remarksIsText = order ? typeof order.remarks !== 'object' : false;

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/orders/${id}`);
        const doc = res.data;
        setOrder(doc);
        setSelectedCustomer({
          id: doc.customer_id,
          customer_name: doc.customer_name,
          firm_name: doc.firm_name,
          mobile_number: doc.contact_number,
          delivery_address: doc.delivery_address,
        });
        setDeliveryAddress(doc.delivery_address || '');
        setOrderDetails({
          priority: doc.priority || 'medium',
          po_ref_number: doc.po_ref_number || '',
          po_date: dateInputValue(doc.po_date),
          order_source: doc.order_source || '',
          discount_pct: doc.discount_pct ?? '',
          gst_pct: doc.gst_pct ?? '',
          remarks: typeof doc.remarks === 'string' ? doc.remarks : '',
        });
        setExpectedDeliveryDate(dateInputValue(doc.expected_delivery_date));
        const live = (doc.line_items || []).filter(item => !item.is_deleted);
        const editable = live.filter(item => !item.is_split && !item.is_split_child);
        setSplitCount(live.length - editable.length);
        setLineItems(editable.map(itemToRow));
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load order'));
      }
      setLoading(false);
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!customerSearch.trim()) return;
    api.get('/customers', { params: { search: customerSearch, limit: 50 } })
      .then(res => setCustomers(res.data.customers))
      .catch(() => {});
  }, [customerSearch]);

  const updateItem = (rowId, patch) => {
    setLineItems(prev => prev.map(item => (item.id === rowId ? { ...item, ...patch } : item)));
  };

  const addDraft = (patch) => {
    setLineItems(prev => [...prev, { ...BLANK_ITEM, quantity: '1', ...patch, id: draftId }]);
    setDraftSeq(n => n + 1);
  };

  const removeItem = (rowId) => setLineItems(prev => prev.filter(item => item.id !== rowId));

  // a booked row is the floor's business: its quantity holds once work has
  // started, and dropping it is an admin's call, the same as deleting one
  // outright. A row booked at nothing is the exception — there is no quantity
  // the floor is running to, and until somebody types one it cannot be loaded
  const lockOf = (item) => ({
    quantity: Boolean(item.booked && item.started && (parseInt(item.quantity) || 0) > 0),
    remove: Boolean(item.booked && (item.started || !isAdmin)),
  });

  const lineItemsTotal = useMemo(
    () => lineItems.reduce((sum, item) => sum + calcItemTotal(item), 0),
    [lineItems],
  );

  const orderTotal = useMemo(() => {
    const taxable = lineItemsTotal - lineItemsTotal * (parseFloat(orderDetails.discount_pct) || 0) / 100;
    return taxable + taxable * (parseFloat(orderDetails.gst_pct) || 0) / 100;
  }, [lineItemsTotal, orderDetails.discount_pct, orderDetails.gst_pct]);

  const startedCount = lineItems.filter(item => item.booked && item.started).length;
  const namedCount = lineItems.filter(item => item.item_name?.trim()).length;

  const createCustomerInline = async () => {
    const errors = {};
    const nameErr = validateField('display_name', newCustomer.display_name, [{ name: 'required', options: { message: 'Firm name is required' } }]);
    if (nameErr) errors.display_name = nameErr;
    const mobileErr = validateField('mobile_number', newCustomer.mobile_number, [{ name: 'mobile', options: { allowEmpty: true } }]);
    if (mobileErr) errors.mobile_number = mobileErr;
    const emailErr = validateField('email', newCustomer.email, [{ name: 'email' }]);
    if (emailErr) errors.email = emailErr;
    setNewCustomerErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the errors in the customer form');
      return;
    }
    try {
      const res = await api.post('/customers', newCustomer);
      setSelectedCustomer(res.data);
      setShowNewCustomer(false);
      setNewCustomerErrors({});
      toast.success('Customer created');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create customer'));
    }
  };

  const save = async () => {
    if (!selectedCustomer) { toast.error('Pick a customer first'); return; }
    const named = lineItems.filter(item => item.item_name?.trim());
    if (named.length === 0) { toast.error('An order must keep at least one item'); return; }
    const uncounted = named.find(item => (parseInt(item.quantity) || 0) < 1);
    if (uncounted) {
      toast.error(`How many ${uncounted.item_name.trim()}? Type the quantity — three boards is 3.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomer.id,
        expected_delivery_date: expectedDeliveryDate ? new Date(expectedDeliveryDate).toISOString() : '',
        priority: orderDetails.priority,
        order_source: orderDetails.order_source || null,
        po_ref_number: orderDetails.po_ref_number,
        po_date: orderDetails.po_date ? new Date(orderDetails.po_date).toISOString() : '',
        delivery_address: deliveryAddress,
        line_items: named.map(item => ({
          id: item.booked ? item.id : null,
          item_name: item.item_name.trim(),
          quantity: parseInt(item.quantity) || 0,
          special_instructions: item.special_instructions || '',
          expected_delivery_date: item.expected_delivery_date || null,
          catalog_item_id: item.catalog_item_id || null,
          ...(canSeeFinancials ? {
            item_value: calcItemTotal(item),
            price_per_piece: parseFloat(item.price_per_piece) || 0,
            price_per_sheet: parseFloat(item.price_per_sheet) || 0,
            pieces_per_sheet: parseInt(item.pieces_per_sheet) || 1,
            price_type: item.price_type,
          } : {}),
        })),
        ...(canSeeFinancials ? {
          order_value: orderTotal,
          discount_pct: parseFloat(orderDetails.discount_pct) || 0,
          gst_pct: parseFloat(orderDetails.gst_pct) || 0,
        } : {}),
        ...(remarksIsText ? { remarks: orderDetails.remarks } : {}),
      };
      await api.put(`/orders/${order.id}`, payload);
      toast.success(`Order ${order.order_id} updated`);
      navigate(`/orders/${order.id}`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save the order'));
    }
    setSaving(false);
  };

  // the desk types Ctrl+S out of habit; hold the same guards the Save button
  // holds, and swallow the chord either way so the browser never offers to save
  // the page instead
  const saveRef = useRef(null);
  saveRef.current = saving || namedCount === 0 ? null : save;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      if (event.key?.toLowerCase() !== 's') return;
      event.preventDefault();
      saveRef.current?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue" /></div>;
  if (!order) return <div className="text-center py-12 text-slate-500">Order not found</div>;

  return (
    <Screen data-testid="edit-order-page">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/orders/${order.id}`)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Edit Order</h1>
            <p className="text-xs text-slate-400">
              {order.order_id} | Voice: {order.voice_number} | Booked {formatIST(order.created_at, { dateOnly: true })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            {namedCount} item{namedCount !== 1 ? 's' : ''}
            {canSeeFinancials && lineItemsTotal > 0 && ` · ₹${lineItemsTotal.toLocaleString()}`}
          </span>
          <Button variant="outline" onClick={() => navigate(`/orders/${order.id}`)}>Cancel</Button>
          <Button onClick={save} disabled={saving || namedCount === 0}
            title={`Save changes (${isMac() ? '⌘' : 'Ctrl+'}S)`}
            className="bg-green-600 hover:bg-green-700 text-white px-6" data-testid="save-order-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {startedCount > 0 && (
        <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {startedCount} item{startedCount !== 1 ? 's are' : ' is'} already on the floor. Names, rates
            and remarks still change; the quantity is what the batch is running to, and the buyer can no
            longer be corrected.
          </span>
        </div>
      )}

      {splitCount > 0 && (
        <p className="text-xs text-slate-500">
          {splitCount} row{splitCount !== 1 ? 's' : ''} from a split job {splitCount !== 1 ? 'are' : 'is'} not
          listed here — a split is worked out on the job card, and its quantities and values stay with it.
        </p>
      )}

      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-h-0 space-y-4 overflow-y-auto pr-1">
          <BuyerPanel
            jobType={order.job_type || 'new'}
            setJobType={() => {}}
            lockJobType
            customerSearch={customerSearch}
            setCustomerSearch={setCustomerSearch}
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            showNewCustomer={showNewCustomer}
            setShowNewCustomer={setShowNewCustomer}
            newCustomer={newCustomer}
            setNewCustomer={setNewCustomer}
            newCustomerErrors={newCustomerErrors}
            customers={customers}
            createCustomerInline={createCustomerInline}
            deliveryAddress={deliveryAddress}
            setDeliveryAddress={setDeliveryAddress}
          />

          <ItemsGrid
            lineItems={lineItems}
            calcItemTotal={calcItemTotal}
            onChange={updateItem}
            onAddDraft={addDraft}
            onRemove={removeItem}
            draftId={draftId}
            customerId={selectedCustomer?.id}
            lockOf={lockOf}
            showMoney={canSeeFinancials}
            hint="Type a product name in the blank row to add an item"
          />

          {remarksIsText && (
            <section className="border border-slate-200 rounded">
              <header className="bg-brand-blue px-3 py-1.5 rounded-t">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-white">Narration</h3>
              </header>
              <Textarea
                value={orderDetails.remarks}
                onChange={e => setOrderDetails(d => ({ ...d, remarks: e.target.value }))}
                placeholder="Anything about this order as a whole — terms, packing notes, who to call on delivery."
                rows={3}
                className="text-sm border-0 rounded-t-none shadow-none focus-visible:ring-1 resize-none"
              />
            </section>
          )}
        </div>

        <OrderStrip
          orderDetails={orderDetails}
          setOrderDetails={setOrderDetails}
          expectedDeliveryDate={expectedDeliveryDate}
          setExpectedDeliveryDate={setExpectedDeliveryDate}
          stakeholderTags={[]}
          setStakeholderTags={() => {}}
          itemsTotal={lineItemsTotal}
          entryDate={formatIST(order.created_at, { dateOnly: true })}
          showAdvance={false}
          showStakeholders={false}
          showMoney={canSeeFinancials}
        />
      </div>
    </Screen>
  );
}
